import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radar, Loader2, CheckCircle2, XCircle } from "lucide-react";

const Check = ({ ok, label, detail }) => (
  <div className={`rounded-lg border p-3 flex items-start gap-2 ${ok ? "border-forest/15 bg-beige" : "border-red-300 bg-red-50"}`}>
    {ok ? <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
    <div className="min-w-0">
      <div className="text-sm text-ink font-medium">{label}</div>
      {detail && <div className="text-xs text-inkmute mt-0.5">{detail}</div>}
    </div>
  </div>
);

export default function GeoTab() {
  const [siteUrl, setSiteUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [checks, setChecks] = useState([]);

  useEffect(() => {
    api.get("/admin/seo/geo-check").then((r) => setChecks(r.data)).catch(() => {});
    api.get("/admin/seo/properties").then((r) => {
      const p = r.data.find((x) => x.is_primary) || r.data[0];
      if (p?.site_url) setSiteUrl((prev) => prev || p.site_url);
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!siteUrl) return toast.error("Enter your site URL");
    setRunning(true);
    try {
      const r = await api.post("/admin/seo/geo-check", { site_url: siteUrl });
      setChecks((prev) => [r.data, ...prev]);
      toast.success(`GEO readiness: ${r.data.score}/100`);
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    setRunning(false);
  };

  const latest = checks[0];

  return (
    <div data-testid="geo-tab">
      <div className="rounded-xl bg-beige border border-forest/10 p-4 mb-6 flex flex-wrap gap-3 items-center">
        <input
          data-testid="geo-site-url"
          value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://pollendots.com"
          className="flex-1 min-w-[240px] bg-cream border border-forest/15 rounded-lg px-4 py-2 text-sm"
        />
        <button data-testid="geo-run" onClick={run} disabled={running}
          className="inline-flex items-center gap-2 bg-forest text-cream px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-forestDeep disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
          {running ? "Checking…" : "Run GEO Check"}
        </button>
      </div>

      <p className="text-sm text-inkmute mb-6 max-w-3xl">
        GEO (Generative Engine Optimisation) readiness — everything here is free, no subscriptions. These are the technical
        fundamentals that decide whether ChatGPT, Claude, Perplexity and Google's AI can read, index and <b>cite</b> your site.
      </p>

      {!latest && <div className="text-inkmute text-sm">No checks yet — run your first GEO readiness check above.</div>}

      {latest && (
        <div data-testid="geo-result" className="space-y-5">
          <div className="rounded-xl border border-forest/10 bg-beige p-5 flex flex-wrap items-center gap-5">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center font-serif text-3xl ${latest.score >= 80 ? "bg-forest text-cream" : latest.score >= 50 ? "bg-pollen text-ink" : "bg-red-500 text-white"}`}>
              {latest.score}
            </div>
            <div>
              <div className="font-serif text-2xl text-ink">GEO readiness — {latest.site_url}</div>
              <div className="text-xs text-inkmute mt-1">{new Date(latest.created_at).toLocaleString()} · {latest.blocked_count} AI crawlers blocked</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Check ok={latest.robots_found} label="robots.txt" detail={latest.robots_found ? "Found and readable" : "Missing — crawler access is undefined"} />
            <Check ok={latest.llms_txt_found} label="llms.txt" detail={latest.llms_txt_found ? "Found — AI models have your content map" : "Missing — the emerging standard for AI citation"} />
            <Check ok={latest.sitemap_found} label="XML sitemap" detail={latest.sitemap_url} />
            <Check ok={latest.schema_found} label="Structured data (JSON-LD)" detail={latest.schema_found ? "Found on homepage" : "Not found on homepage"} />
            <Check ok={latest.og_found} label="Open Graph tags" detail={latest.og_found ? "Found on homepage" : "Not found on homepage"} />
            <Check ok={latest.blocked_count === 0} label="AI crawler access" detail={latest.blocked_count === 0 ? "All major AI crawlers allowed" : `${latest.blocked_count} blocked in robots.txt`} />
          </div>

          <div className="rounded-xl border border-forest/10 bg-beige p-5">
            <div className="text-[10px] uppercase tracking-widest text-clay mb-3">AI crawler access — who can read your site</div>
            <div className="flex flex-wrap gap-2">
              {(latest.bots || []).map((b) => (
                <span key={b.bot} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${b.blocked ? "bg-red-500 text-white" : "bg-forest text-cream"}`}>
                  {b.bot} {b.blocked ? "· blocked" : ""}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-pollen/60 bg-cream p-5">
            <div className="text-[10px] uppercase tracking-widest text-clay mb-3">What to do next (all free)</div>
            <ol className="space-y-2 list-decimal ml-5">
              {(latest.recommendations || []).map((r, i) => <li key={i} className="text-sm text-ink leading-relaxed">{r}</li>)}
            </ol>
          </div>

          {checks.length > 1 && (
            <div className="text-xs text-inkmute">
              Previous checks: {checks.slice(1).map((c) => `${c.score}/100 (${new Date(c.created_at).toLocaleDateString()})`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
